import { logError } from '@/lib/logError';
import rest from '@/lib/rest';
import { useUserStore } from '@/stores/user';
import { defineStore } from 'pinia';
import { reactive } from 'vue';

/**
 * Survey answers as customer.io attributes. The names and value shapes match the
 * pre-2.0 Svelte onboarding, so existing customer.io segments keep working:
 * `findUs` is the option value, or the free text when "Other" was chosen.
 */
export type SurveyAttributes = { findUs?: string; genre?: string };

export const useOnboardingStore = defineStore('onboarding', () => {
  const userStore = useUserStore();

  const state = reactive({
    discoveryMethod: '',
    selectedGenres: [] as string[],
    otherDiscoveryMethodText: '',
    isLoading: false,
  });

  const setDiscoveryMethod = (method: string) => {
    state.discoveryMethod = method;
  };
  const setSelectedGenres = (genres: string[]) => {
    state.selectedGenres = genres;
  };
  const setOtherDiscoveryMethodText = (text: string) => {
    state.otherDiscoveryMethodText = text;
  };

  // Fire-and-forget: dabble-rest forwards the attributes to customer.io. A lost
  // survey answer must never block onboarding, so this is not awaited by the
  // views and a failure is only logged.
  const reportSurvey = (attributes: SurveyAttributes) => {
    const request = rest
      .post('analytics/survey')
      .header('x-uid', userStore.user?.uid || '')
      .credentials('include');
    // The genre answer is posted as the page leaves for the writer (the trial
    // signup path redirects on `next`). keepalive lets the browser finish the
    // request after unload instead of aborting it.
    request.init.keepalive = true;
    request
      .send({ attributes })
      .catch((error: unknown) => logError('Failed to report onboarding survey answer:', error));
  };

  return { state, setDiscoveryMethod, setSelectedGenres, setOtherDiscoveryMethodText, reportSurvey };
});
